import { createAsyncThunk } from "@reduxjs/toolkit";
import OnboardingApiService, {
  toFriendlyOnboardingError,
} from "../apiServices/OnboardingApiService";
import { ApiEnvelopeError } from "../helpers/ApiEnvelopeHelper";
import {
  CreateCaptainPayload,
  DraftBusinessForm,
  PendingBusiness,
  RecordSettlementPayload,
  PayoutDetails,
  RequestSettlementInput,
  SettleCaptainPayload,
} from "../../interfaces/OnboardingObject";
import { fetchNineSetups } from "./NineSetupThunk";

/**
 * Gate call for the Onboarding Program tab. Rejects with the RAW errorCode
 * (not friendly copy) so the slice can distinguish CAPTAIN_NOT_FOUND — which
 * is a state ("not enrolled"), not an error — from real failures.
 */
export const fetchCaptainSummary = createAsyncThunk(
  "onboarding/fetchCaptainSummary",
  async (_: void, { rejectWithValue }) => {
    try {
      return await OnboardingApiService.getCaptainSummary();
    } catch (error) {
      if (error instanceof ApiEnvelopeError) {
        return rejectWithValue(error.errorCode);
      }
      // Plain network errors (fetch throw, offline) → friendly copy, not
      // "TypeError: Failed to fetch".
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

/** Fetches ALL of the captain's submissions; status chips filter client-side. */
export const fetchPendingBusinesses = createAsyncThunk(
  "onboarding/fetchPendingBusinesses",
  async (_: void, { rejectWithValue }) => {
    try {
      return await OnboardingApiService.getPendingBusinesses();
    } catch (error) {
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

export const createPendingBusiness = createAsyncThunk(
  "onboarding/createPendingBusiness",
  async (form: DraftBusinessForm, { dispatch, rejectWithValue }) => {
    try {
      const created = await OnboardingApiService.createPendingBusiness(form);
      // Refresh pendingOnboardings on the summary card (cheap; counts are
      // computed at read time server-side). Fire-and-forget.
      dispatch(fetchCaptainSummary());
      // The backend auto-transitions the linked nine_setup row to
      // draft_submitted as a side effect of this call — refetch so the
      // Nine Setup list doesn't show a stale status until manual refresh.
      if (form.setupId) {
        dispatch(fetchNineSetups(undefined));
      }
      return created;
    } catch (error) {
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

/**
 * Fresh payment link for an UNPAID row. Calls retry-payment-link (keyed only
 * on pendingId, see OnboardingApiService.retryPaymentLink) and merges the
 * returned payment fields onto the row the caller already has — the endpoint
 * doesn't echo businessName/contactPhone/etc., so a naive replace would blank
 * them out. Takes the full row (not just an id) so both call sites —
 * PendingBusinessCard (list) and CreateDraftBusinessModal (fresh submit) —
 * can dispatch it with whatever row they're holding, in or out of the store.
 */
export const regeneratePaymentLink = createAsyncThunk(
  "onboarding/regeneratePaymentLink",
  async (row: PendingBusiness, { rejectWithValue }) => {
    try {
      const result = await OnboardingApiService.retryPaymentLink(row.pendingId);
      const merged: PendingBusiness = {
        ...row,
        paymentRequired: result.paymentRequired,
        paymentStatus: result.paymentStatus,
        paymentShortUrl: result.shortUrl,
        paymentLinkExpiresAt: result.expiresAt,
      };
      return merged;
    } catch (error) {
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

export const createCaptain = createAsyncThunk(
  "onboarding/createCaptain",
  async (payload: CreateCaptainPayload, { rejectWithValue }) => {
    try {
      return await OnboardingApiService.createCaptain(payload);
    } catch (error) {
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

export const updateCaptain = createAsyncThunk(
  "onboarding/updateCaptain",
  async (
    { captainId, payload }: { captainId: string; payload: import("../../interfaces/OnboardingObject").UpdateCaptainPayload },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const updated = await OnboardingApiService.updateCaptain(captainId, payload);
      dispatch(fetchMyTeam());
      return updated;
    } catch (error) {
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

export const fetchMyTeam = createAsyncThunk(
  "onboarding/fetchMyTeam",
  async (_: void, { rejectWithValue }) => {
    try {
      return await OnboardingApiService.getCaptainsByLead();
    } catch (error) {
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

/** Records a payout for one captain/lead. The component blocks double-submit
 *  via the per-row loadingMap key the slice sets from meta.arg.captainId. */
export const settleCaptain = createAsyncThunk(
  "onboarding/settleCaptain",
  async (payload: SettleCaptainPayload, { dispatch, rejectWithValue }) => {
    try {
      const settled = await OnboardingApiService.settleCaptain(payload);
      // Whether the payout closed that member's pending request is the
      // server's call (a partial payout may not) — re-read instead of guessing.
      dispatch(fetchTeamRequests());
      return settled;
    } catch (error) {
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

/** Records a payout to a team member by user id (the generic settlements
 *  API — closes their pending request server-side). The response is only the
 *  new settlement, so the roster and requests are re-read afterwards.
 *  Component blocks double-submit via the per-payee loadingMap key. */
export const recordSettlement = createAsyncThunk(
  "onboarding/recordSettlement",
  async (payload: RecordSettlementPayload, { dispatch, rejectWithValue }) => {
    try {
      const settlement = await OnboardingApiService.recordSettlement(payload);
      dispatch(fetchMyTeam());
      dispatch(fetchTeamRequests());
      return settlement;
    } catch (error) {
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

/** The caller's own payout history, newest first. */
export const fetchSettlements = createAsyncThunk(
  "onboarding/fetchSettlements",
  async (_: void, { rejectWithValue }) => {
    try {
      return await OnboardingApiService.getSettlements();
    } catch (error) {
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

/** The caller's own PENDING settlement request, or null when there is none. */
export const fetchMyPendingRequest = createAsyncThunk(
  "onboarding/fetchMyPendingRequest",
  async (_: void, { rejectWithValue }) => {
    try {
      const requests = await OnboardingApiService.getSettlementRequests("MINE");
      return requests[0] ?? null;
    } catch (error) {
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

/** PENDING requests the caller has to pay (a lead's team), shown as "Requested" on the roster. */
export const fetchTeamRequests = createAsyncThunk(
  "onboarding/fetchTeamRequests",
  async (_: void, { rejectWithValue }) => {
    try {
      return await OnboardingApiService.getSettlementRequests("TO_PAY");
    } catch (error) {
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

/** Payout details from the caller's most recent settled request, to prefill
 *  the next request. Null when there is none (or none with details). */
export const fetchLastPayout = createAsyncThunk<PayoutDetails | null, void, { rejectValue: string }>(
  "onboarding/fetchLastPayout",
  async (_, { rejectWithValue }) => {
    try {
      const settled = await OnboardingApiService.getSettlementRequests("MINE", "SETTLED");
      return settled.find((request) => request.payout)?.payout ?? null;
    } catch (error) {
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

/** One-tap "Request settlement". The server derives the amount. */
export const requestSettlement = createAsyncThunk(
  "onboarding/requestSettlement",
  async (input: RequestSettlementInput, { dispatch, rejectWithValue }) => {
    try {
      return await OnboardingApiService.requestSettlement(input);
    } catch (error) {
      // The screen didn't know a request was already open (another device, a
      // stale tab) — pull it in so the card flips to "Request sent".
      if (
        error instanceof ApiEnvelopeError &&
        error.errorCode === "REQUEST_ALREADY_PENDING"
      ) {
        dispatch(fetchMyPendingRequest());
      }
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

export const cancelSettlementRequest = createAsyncThunk(
  "onboarding/cancelSettlementRequest",
  async (requestId: string, { rejectWithValue }) => {
    try {
      return await OnboardingApiService.cancelSettlementRequest(requestId);
    } catch (error) {
      return rejectWithValue(toFriendlyOnboardingError(error));
    }
  }
);

const OnboardingThunk = {
  fetchCaptainSummary,
  fetchPendingBusinesses,
  createPendingBusiness,
  regeneratePaymentLink,
  createCaptain,
  fetchMyTeam,
  settleCaptain,
  recordSettlement,
  fetchSettlements,
  fetchMyPendingRequest,
  fetchTeamRequests,
  requestSettlement,
  cancelSettlementRequest,
};

export default OnboardingThunk;
